import moment from 'moment'
import { EmploymentStatuses, LicenseRestrictions, Regex, home_contents_loss_and_cause_claim_types } from '../utilities/enums'
import { Helpers } from '../utilities/helpers'

const { replaceAllInString } = Helpers

const luhnCheck = (val) => {
  // Luhn Check Code from https://gist.github.com/4075533
  // accept only digits, dashes or spaces
  const numericDashRegex = /^[\d\-\s]+$/
  if (!numericDashRegex.test(val)) {
    return false
  }

  // The Luhn Algorithm
  let nCheck = 0
  let nDigit = 0
  let bEven = false
  let strippedField = val.replace(/\D/g, '')

  for (let n = strippedField.length - 1; n >= 0; n--) {
    let cDigit = strippedField.charAt(n)
    nDigit = parseInt(cDigit, 10)

    if (bEven) {
      if ((nDigit *= 2) > 9) {
        nDigit -= 9
      }
    }

    nCheck += nDigit
    bEven = !bEven
  }

  return nCheck % 10 === 0
}

export default class Validators {
  //user_id
  static isUserID = id => id.length === 24

  // email
  static isEmail = email => {
    // Check if the email is not empty and does not start with a space
    if (!email || email?.trim() !== email) {
      return false
    }
    
    return Regex.email.test(email)
  }

  // cell
  static isCellNo = cell => Regex.cellNumber.test(cell)

  // landline or cell
  static isLandLineOrCellNo = number => Regex.numeric.test(number) && number.length === 10 && number[0] === '0'

  // name
  static isName = name => name && name.trim().length > 1 && Regex.alphaSpaceDash.test(name)

  //full name
  static isFullName = name => {
    const names = name.split(' ');
    return names.length >= 2 && names.every((n) => this.isName(n));
  }

  // id no
  static isZaId = (zaId) => {
    if (!Regex.zaId.test(zaId)) {
      return false
    }

    if (!luhnCheck(zaId)) {
      return false
    }

    // must be older than 18
    const parsedInfo = Helpers.parseSAIdNo(zaId)

    if (!parsedInfo || parsedInfo.age < 18) {
      return false
    }

    return true
  }

  // year
  static isYear = year => year.length === 4 && Regex.numeric.test(year) && [1, 2].indexOf(parseInt(year[0], 10)) > -1

  // number
  static isNumeric = num => Regex.numeric.test(num)

  // number plate
  static isNumberPlate = reg => {
    const str = Helpers.replaceAllInString(reg, ' ', '')

    // Check & restrict user if the number plate is only numbers
    if (/^[0-9]+$/.test(str)) {
      return false
    }

    if (reg && reg.substr(reg.length - 3) === 'TMP') {
      return true
    } else {
      return reg && Regex.alphanumericSpace.test(reg) && str.length > 3 && str.length < 10
    }
  }

  // vin
  static isVin = vin => Regex.vin.test(vin)

  // engine number
  static isEngineNo = engineNo => Regex.engineNo.test(engineNo)

  // ZA bank account basic validation
  static isZaBankAcc = no => Regex.numeric.test(no) && (no.length >= 6)

  // ZA company registration number
  static isZaCompanyRegistrationNo = no => Regex.zaCompanyRegistrationNumer.test(no)

  // ZA VAT number
  static isZaVatNumber = no => Regex.zaVatNumber.test(no)

  // Dealership Code
  static isDealershipCode = code => code && code.trim().length === 4

  // Password
  static isPassword = password => password && password.trim().length >= 6

  //street name
  static isStreetName = street => Regex.streetName.test(street)

  // Employment status
  static isValidEmploymentStatus = (val) => {
    const statuses = EmploymentStatuses.map(status => status.value)

    return statuses.indexOf(val) > -1
  }

  // License restrictions
  static isValidLicenseRestriction = (val) => {
    const statuses = LicenseRestrictions.map(status => status.value)

    return statuses.indexOf(val) > -1
  }

  // Special characters
  static containsSpecialCharacter = (value, character) => {
    if (value.includes(character)) {
      value = value.replace(/\s/g, '');

      return true
    }

    return false
  }

  // Is value in range validation
  static isValueInRange = (value, min, max) => {
    return (value > min && value < max)
  }

  // claim type
  static isValidClaimType = (val) => {
    return val === 'motor' || val === 'all-risk' || val === 'home-contents'
  }

  // loss type
  static isValidLossType = (val) => {
    return val === 'theft' || val === 'damage' || val === 'lost-item'
  }

  static isValidLossTypeMotor = (val) => {
    return val === 'DQ' ||
      val === 'DS' ||
      val === 'DT' ||
      val === 'DU' ||
      val === 'EJ'
  }

  static isValidLossTypeHomeContents = (val) => {
    const isValid = home_contents_loss_and_cause_claim_types.some((loss) => loss.value === val)
    return isValid
  }

  static isValidCauseTypeHomeContents = (lossType, causeType) => {
    const lossTypeList = home_contents_loss_and_cause_claim_types.filter((loss) => loss.value === lossType)

    const causeTypeList = lossTypeList[0].causeTypes

    const isValid = causeTypeList.some((cause) => cause.value === causeType)

    return isValid
  }

  static isValidRole = (val) => {
    return val === 'claims' || val === 'sales' || val === 'retentions'
  }

  // incident type
  static isValidCauseTypeMotor = (val) => {
    return val === 'COD' ||
      val === 'CTC' ||
      val === 'CTO' ||
      val === 'KEY' ||
      val === 'MBB' ||
      val === 'MMD' ||
      val === 'PTH' ||
      val === 'ROT' ||
      val === 'WHA' ||
      val === 'WSF' ||
      val === 'WST' ||
      val === 'TFA' ||
      val === 'TFD' ||
      val === 'TFE' ||
      val === 'TFJ' ||
      val === 'TFV' ||
      val === 'AWR' ||
      val === 'AWS' ||
      val === 'MPL' ||
      val === 'CTP'
  }

  static isValidIncidentTime = (timeObj, dateString) => {
    let isValidTime = !isNaN(Date.parse(timeObj))

    const dateFromDateString = moment(dateString, 'YYYY-MM-DD');
    // Combine the date and time
    const combinedDateTime = moment(dateFromDateString).set({
      'hour': timeObj.hours(),
      'minute': timeObj.minutes(),
      'second': timeObj.seconds(),
    });
    const currentDateTime = moment()

    let isNotInTheFuture = combinedDateTime.isBefore(currentDateTime)
    return isValidTime && isNotInTheFuture
  }

  //getDOB
  static getDob = (idNumber) => {
    var Year = idNumber.substring(0, 2);
    var Month = idNumber.substring(2, 4)
    var Day = idNumber.substring(4, 6)

    var cutoff = (new Date()).getFullYear() - 2000

    var dob = (Year > cutoff ? '19' : '20') + Year + '-' + Month + '-' + Day;

    return dob;
  }

  // License date
  static isValidLicenseDate = (date, zaId) => {
    //1. parse ZA id number
    let parsedZaIdInfo

    if (!date) {
      return false
    }

    if (zaId) {
      parsedZaIdInfo = Helpers.parseSAIdNo(zaId)
      const { year, month, day } = parsedZaIdInfo

      //birth date//
      const birthDate = moment(this.getDob(zaId))

      const licenseIssueDate = moment(date)
      const licenseAge = moment(date).diff(birthDate, 'y', true)

      return !Number.isNaN(licenseAge) && licenseAge >= 18
    } else {
      return date instanceof Date
    }
  }

  // validate date
  static isValidDate = (date) => {
    return (new Date(date) !== 'Invalid Date') && !isNaN(new Date(date));
  }

  // Previous losses
  static isValidPreviousLosses = (prvLosses) => {
    // is an array actually being supplied?
    if (!prvLosses || !Array.isArray(prvLosses)) {
      return false
    }

    // check for malformed data in array
    const containsMalformedData = prvLosses.some((loss) => {
      if (loss.prevLossSection === 'STCNT') {
        return !loss.prevLossCategory // no loss category
      } else {
        return !loss.prevLossDetails // no description
          || loss.prevLossDetails.trim().length < 10 // description less than 10 characters
          || !loss.prevLossAmount // no amount specified
      }
    })

    return !containsMalformedData
  }

  static latestYear = (year) => {
    const date = new Date()
    let currentyear = date.getFullYear()
    return (year && this.isYear(year) && year <= currentyear)
  }

  static isValidAutomaticVehicleDefiner = (state) => {
    let isValid = false
    if (state.automaticDefinerNotFound) {
      isValid = state.regularDriver
        ? !state.iWyzePersonExists ||
        state.iwyzePerson.licenseRestrictionsCode !== 'AUTO'
        : state.notTheRegularDriver
          ? state.regDrvLicenseRestrictions !== 'AUTO'
          : true
    }

    if (!isValid) {
      isValid = (state.isEdittingVehicle && !state.showAutomaticQuestion) ||
        state.vehicleIsAutomatic != null;
    }

    return isValid
  }

  //building & contents validators
  static isBuildingName = (str) => str ? str.trim().length > 0 : false

  static isUnitNumber = (str) => str && str.trim() ? Regex.alphanumeric.test(str.trim()) : false;

}